import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  Box,
  TextField,
  Typography,
  Paper,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlock } from '@fortawesome/free-solid-svg-icons/faUnlock';
import { faCircleQuestion, faLock } from '@fortawesome/free-solid-svg-icons';
import { PagesContext, PageSetting } from '../../../../contexts/PagesContext';

import SwitchButton from '../../../Buttons/SwitchButton';

interface ChangePasswordProps {
  pageSettings: PageSetting;
  isSwitchOn: boolean;
  toggleSaveButton: (value: boolean) => void;
  setIsSwitchOn: (value: boolean) => void;
  onPasswordChange: (password: string) => void;
  onDescriptionTextChange: (password: string) => void;
}

const PasswordProtection: React.FC<ChangePasswordProps> = ({
  onPasswordChange,
  onDescriptionTextChange,
  pageSettings,
  isSwitchOn,
  setIsSwitchOn,
  toggleSaveButton,
}) => {
  const switchRef = useRef<HTMLInputElement>(null);
  const { contextErrors } = useContext(PagesContext);

  const [initialPasswordState, _ps] = useState(isSwitchOn);

  const [newPassword, setNewPassword] = React.useState(pageSettings.password ?? '');
  const [newDescriptionText, setNewDescriptionText] = React.useState(pageSettings.auth_page_description ?? '');

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
  };

  const handleDescriptionTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewDescriptionText(event.target.value);
  };

  const handleDomainSwitchClick = () => {
    if (switchRef.current) {
      if (!switchRef.current.checked) {
        setIsSwitchOn(false);
      } else {
        setIsSwitchOn(true);
      }
    }
  };

  useEffect(() => {
    onPasswordChange(newPassword);
    onDescriptionTextChange(newDescriptionText);
  }, [newPassword, newDescriptionText]);

  useEffect(() => {
    if (!newPassword && isSwitchOn) {
      toggleSaveButton(true);
      return;
    }

    toggleSaveButton(false);
  }, [toggleSaveButton, isSwitchOn, newPassword]);

  return (
    <>
      <Box>
        <h2 className="mb-2">Password Protection</h2>
      </Box>
      <Box sx={{ my: 2 }}>
        Restrict access to this page with a password.
      </Box>

      <Box color="red">
        Changes will take effect after you publish the page.
      </Box>

      <hr className="mt-3" />

      <div className="d-flex align-content-center justify-content-around w-75 mb-3">
        <span className="text-bold">
          Off
        </span>

        <span className="d-flex align-content-center flex-wrap">
          <SwitchButton
            id="is_password_protected"
            defaultChecked={initialPasswordState}
            onClick={handleDomainSwitchClick}
            inputRef={switchRef}
          />
        </span>

        <span>
          On
        </span>
      </div>

      {isSwitchOn && (
        <Box>
          <hr />

          <Paper variant="outlined" sx={{ padding: 2, backgroundColor: '#f9f9f9', textAlign: 'center' }}>
            <FontAwesomeIcon icon={!initialPasswordState ? faUnlock : faLock} className="px-2" />
            {!initialPasswordState ? 'This page is currently not password protected' : 'This page is currently password protected'}
          </Paper>
          <Box mt={4} display="flex">
            <Box sx={{ width: '200px', fontWeight: 'bold' }}>
              Password

              <span
                style={{
                  color: '#bb0000',
                  fontSize: '80%',
                  marginLeft: '4px',
                  padding: '4px',
                  borderRadius: '4px',
                  backgroundColor: '#fff0f0',
                }}
              >
                Required
              </span>
            </Box>
            <TextField
              name="password"
              size="small"
              sx={{ width: '28%' }}
              type="password"
              placeholder="e.g. abcd123!@#"
              value={newPassword}
              onChange={handlePasswordChange}
              inputProps={{ translate: 'no', className: 'notranslate' }}
              helperText={(
                <>
                  4 to 16 characters.
                  <br />
                  Alphanumeric and symbols allowed.
                </>
              )}
              variant="outlined"
              error={!!contextErrors?.password}
            />
          </Box>
          <hr className="mt-3" />
          <Box mt={4} display="flex">
            <Box sx={{ width: '200px', fontWeight: 'bold' }}>
              Display Text
              <FontAwesomeIcon icon={faCircleQuestion} className="px-2" />
              (Optional)
            </Box>
            <TextField
              size="small"
              placeholder="Please enter the password to view this page"
              value={newDescriptionText}
              onChange={handleDescriptionTextChange}
              inputProps={{ translate: 'no', className: 'notranslate' }}
              helperText="Max 255 characters. (Not shown in preview. Please check the published page.)"
              variant="outlined"
              multiline
              rows={2}
              error={!!contextErrors?.auth_page_description}
            />
          </Box>
          <Box mt={4}>
            <Typography variant="caption" style={{ lineHeight: '0', marginTop: '20px' }}>
              *Password protection will be removed if the relevant plan is cancelled.
              <br />
              *We are not liable for data leaks or damages resulting from password misuse.
              <br />
              *Duplicated pages will not retain password protection.
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default PasswordProtection;
